/**
 * PostDetail 스타일 — 매거진 조판
 * @description 넓은 그림 한 장이 머리를 잡고, 그 아래 왼쪽에 곁글(주제 · 글쓴이 · 날짜 · 읽는
 *   시간), 오른쪽에 표제 · 리드 · 본문이 선다. 표제는 랜딩 브랜드 서체(Zalando, 확장체)라
 *   본문(Pretendard)과 대비가 난다 — 그 대비가 매거진의 얼굴이다.
 *
 *   글줄이 서는 열은 한 줄 70자 안팎의 680. 그림은 그 열보다 넓게 튀어나온다(`.fw-prose`).
 */
export const postDetailStyles = {
  article:
    "mx-auto flex w-full max-w-[1100px] flex-col gap-[var(--fw-space-gap-xxl)] pt-[var(--fw-space-inset-md)] pb-[var(--fw-space-inset-xxxl)] tablet:gap-[var(--fw-space-gap-xxxl)] tablet:pt-[var(--fw-space-inset-xl)]",
  /** 커버 — 조판 폭(1100) 안에서 모서리가 둥근 한 장 */
  hero: "overflow-hidden rounded-[var(--fw-radius-xl)]",
  /** 20:9 — `Media` 의 다섯 비율에 없는 값이라 여기서 상자를 잡는다 */
  heroWide: "relative aspect-[20/9] w-full",
  /** 곁글과 본문의 두 열 — 데스크톱부터. 그 전에는 위아래로 쌓인다 */
  layout:
    "grid grid-cols-1 desktop:grid-cols-[240px_minmax(0,1fr)] desktop:gap-[var(--fw-space-gap-xxxl)]",
  /** 곁글 자리 — 데스크톱에서만 격자 한 칸을 차지한다 */
  metaSlot: "hidden desktop:block",
  /** 글줄 열 — 본문 폭을 잠그고 가운데 둔다(태블릿에서 왼쪽으로 쏠리지 않게) */
  column:
    "mx-auto flex w-full max-w-[680px] flex-col gap-[var(--fw-space-gap-xl)]",
  header: "flex flex-col gap-[var(--fw-space-gap-lg)]",
  /** 표제 위의 눈썹 — 폰·태블릿에서만. 데스크톱은 곁글이 주제를 맡는다 */
  eyebrow:
    "fw-caption-strong uppercase tracking-[0.12em] text-[var(--fw-color-content-brand-primary)] desktop:hidden",
  title:
    "font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-[var(--fw-color-content-neutral-primary)] tablet:text-5xl desktop:text-6xl",
  lede: "fw-h3 font-normal text-[var(--fw-color-content-neutral-secondary)]",
  /** 리드 아래 바이라인 — 폰·태블릿에서만. 위에 실선 하나로 리드와 가른다 */
  byline: {
    root: "flex items-center gap-[var(--fw-space-gap-md)] border-t-[length:var(--fw-border-sm)] border-[var(--fw-color-border-subtle)] pt-[var(--fw-space-inset-lg)] desktop:hidden",
    texts: "flex min-w-0 flex-col",
    name: "fw-l2 truncate text-[var(--fw-color-content-neutral-primary)]",
    meta: "fw-caption text-[var(--fw-color-content-neutral-tertiary)]",
  },
  meta: {
    /** 데스크톱에서만 — 그 아래에서는 눈썹과 바이라인이 대신한다 */
    root: "hidden desktop:sticky desktop:top-[calc(var(--fw-header-height)+var(--fw-space-inset-xl))] desktop:flex desktop:flex-col",
    /** 한 줄 — 위에 실선, 이름표와 값 */
    row: "flex flex-col gap-[var(--fw-space-gap-xxs)] border-t-[length:var(--fw-border-sm)] border-[var(--fw-color-border-subtle)] py-[var(--fw-space-inset-md)]",
    label:
      "fw-caption-strong uppercase tracking-[0.12em] text-[var(--fw-color-content-neutral-tertiary)]",
    value: "fw-l2 text-[var(--fw-color-content-neutral-primary)]",
    topic:
      "fw-caption-strong uppercase tracking-[0.12em] text-[var(--fw-color-content-brand-primary)]",
    author: "flex items-center gap-[var(--fw-space-gap-sm)]",
    /** 폰에서는 곁글이 가로로 흐른다 — 세로로 쌓으면 표제까지 너무 멀다 */
    phoneRow:
      "flex flex-wrap gap-x-[var(--fw-space-gap-xl)] gap-y-[var(--fw-space-gap-sm)] desktop:hidden",
    desktopRows: "hidden desktop:flex desktop:flex-col",
  },
  cta: {
    root: "flex flex-col gap-[var(--fw-space-gap-md)] tablet:flex-row tablet:items-center tablet:justify-between",
    texts: "flex flex-col gap-[var(--fw-space-gap-xxs)]",
    title: "fw-l1 text-[var(--fw-color-content-neutral-primary)]",
    description: "fw-b2 text-[var(--fw-color-content-neutral-tertiary)]",
  },
  /** 헤더 오른쪽 — 언어 전환 링크와 공유. 둘 다 ghost 라 스스로 여백을 가져 간격을 두지 않는다 */
  headerActions: "flex items-center",
  /**
   * 언어 전환 링크 — DS ghost 버튼의 모양을 링크에 입힌다. 버튼 스타일의 hover·눌림은
   * `:enabled` 에만 걸려 링크에는 닿지 않아 여기서 다시 건다
   */
  languageLink:
    "hover:bg-[var(--btn-surface-hovered)] active:bg-[var(--btn-surface-pressed)]",
  related: {
    root: "flex w-full flex-col gap-[var(--fw-space-gap-lg)] border-t-[length:var(--fw-border-sm)] border-[var(--fw-color-border-subtle)] pt-[var(--fw-space-inset-xxl)]",
    title:
      "font-heading text-2xl font-extrabold tracking-tight text-[var(--fw-color-content-neutral-primary)]",
    grid: "grid grid-cols-1 gap-[var(--fw-space-gap-lg)] tablet:grid-cols-3",
  },
};

/** 글쓴이 얼굴 — 시안의 32 (DS `xssm`) */
export const POST_AUTHOR_AVATAR_SIZE = "xssm" as const;
export const POST_AUTHOR_AVATAR_WIDTH = 32;
/** 히어로가 가장 넓게 서는 폭 */
export const POST_HERO_SIZES = "(min-width: 1100px) 1100px, 100vw";
/** 바닥의 세 장이 가장 넓게 서는 폭 */
export const POST_RELATED_SIZES =
  "(min-width: 1100px) 360px, (min-width: 768px) 33vw, 100vw";
